/**
 * schema.org structured data for the PDP (VIP-12038).
 *
 * Emitted as a JSON-LD script through unhead, so it is part of the
 * server-rendered response. The template's scattered microdata (itemprop
 * attributes across ProductStage, Price, AggregatedRating, …) predates this
 * and stays untouched; both formats derive from the same v2 payload, so
 * their values agree.
 */
import type { Product } from '@/modules/types/product/ProductSchema.types'
import { isInStock } from '@/services/helpers/ProductHelper'
import UtilityHelper from '@/services/helpers/UtilityHelper'
import SiteConfig from '@/services/SiteConfig'

/**
 * The Product JSON-LD for a loaded v2 product, as an escaped string ready
 * for an inline `<script type="application/ld+json">`.
 */
export const buildProductJsonLd = (
  product: Product,
  /**
   * Page canonical, already resolved by the caller — the API omits
   * canonical_for on master campaigns, where the page still has a
   * (self-referencing) URL (VIP-12038).
   */
  canonicalUrl?: string | null
): string => {
  const images = [
    product.images?.image &&
      UtilityHelper.prefixImage(product.images.image, true).retina_src,
    product.images?.small_image &&
      UtilityHelper.prefixImage(product.images.small_image, false).retina_src
  ].filter(Boolean)

  const description = _description(product)
  const priceValidUntil = _priceValidUntil(product)
  const facts = _wineFacts(product)

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    ...(product.name && { name: product.name }),
    ...(images.length > 0 && { image: images }),
    ...(description && { description }),
    ...(product.sku && { sku: product.sku }),
    ...(product.manufacturer_name && {
      brand: { '@type': 'Brand', name: product.manufacturer_name }
    }),
    ...(facts.length > 0 && { additionalProperty: facts }),
    offers: {
      '@type': 'Offer',
      price: product.final_price,
      priceCurrency: SiteConfig.currencyConfig.currency,
      availability: isInStock(product)
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: SiteConfig.name },
      ...(priceValidUntil && { priceValidUntil }),
      ...(canonicalUrl ? { url: canonicalUrl } : {})
    },
    ...(product.customer_rating_count
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.customer_rating,
            reviewCount: product.customer_rating_count
          }
        }
      : {})
  }

  // `<` must never appear raw inside an inline script — a description
  // containing “</script>” would end the tag mid-payload. Same escaping as
  // the SSR pinia state (entry-server).
  return JSON.stringify(jsonLd).replaceAll('<', String.raw`\u003c`)
}

/**
 * Mirrors what the page itself presents as the description: the meta
 * description, or — like the template's itemprop="description" in
 * ProductStage — the plain text of reason_why_one.
 */
const _description = (product: Product): string | null => {
  if (product.meta_description) return product.meta_description
  if (product.reason_why_one) return _stripHtml(product.reason_why_one)
  return null
}

// reason_why_one is CMS-maintained HTML (rendered via v-html). This runs on
// the server too, so there is no DOM to lean on — tags and the common
// entities are handled by hand. Entities decode *after* tag stripping, so a
// literal &lt;script&gt; can never turn into a strippable (or live) tag.
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' '
}

const _stripHtml = (html: string): string =>
  html
    // [^<>], not [^>]: excluding `<` too keeps a failed match cheap. With
    // `[^>]*` an unclosed `<` lets the run span the rest of the string and
    // backtrack over it at every start position — quadratic on text that
    // contains a stray `<` (Sonar S5852).
    .replace(/<[^<>]*>/g, ' ')
    .replace(
      /&(?:amp|lt|gt|quot|#39|apos|nbsp);/g,
      (entity) => ENTITIES[entity]
    )
    .replace(/\s+/g, ' ')
    // A literal space, since the collapse above left no run longer than one —
    // `\s+` here would rescan the same whitespace for every start position.
    .replace(/ ([.,;:!?])/g, '$1')
    .trim()

/**
 * A dated price offer: the special-price window end, or the campaign's
 * valid_until. Google's product rich results expect priceValidUntil where a
 * price has a known horizon.
 */
const _priceValidUntil = (product: Product): string | null => {
  let source: Date | null = null
  if (product.special_price != undefined && product.special_price_to) {
    source = new Date(product.special_price_to * 1000)
  } else if (product.valid_until) {
    source = new Date(product.valid_until)
  }
  if (source == undefined || Number.isNaN(source.getTime())) return null
  return source.toISOString().slice(0, 10)
}

/**
 * The wine facts the page renders as characteristics, as machine-readable
 * PropertyValue pairs — the payload AI assistants and comparison engines
 * extract instead of parsing German prose. Labels are German like the page
 * itself (all three shops render German).
 */
const _wineFacts = (
  product: Product
): Array<{ '@type': string; name: string; value: string }> => {
  const details = product.details ?? ({} as Product['details'])
  const facts: Array<[string, unknown]> = [
    [
      'Rebsorte',
      details.wine_grape_variety ?? details.wine_grape_variety_cuvee
    ],
    ['Jahrgang', details.wine_year],
    ['Region', details.region],
    ['Land', details.country],
    ['Weinart', details.wine_type],
    ['Geschmack', details.wine_sub_type],
    [
      'Alkoholgehalt',
      typeof product.alcohol_content === 'number' && product.alcohol_content > 0
        ? `${product.alcohol_content}% vol`
        : null
    ],
    [
      'Inhalt',
      product.reference_unit_contents && product.reference_unit
        ? `${product.reference_unit_contents} ${product.reference_unit}`
        : null
    ]
  ]

  return facts
    .filter(([, value]) => value != undefined && value !== '')
    .map(([name, value]) => ({
      '@type': 'PropertyValue',
      name,
      value: String(value)
    }))
}
